import React from 'react';

const container = {
  minHeight: '100vh',
  background: '#050516',
  color: '#e2e8f0',
  fontFamily: 'sans-serif',
};
const card = {
  maxWidth: 900,
  margin: '0 auto',
  padding: '40px 24px 60px',
};
const section = { marginBottom: 28 };
const h1 = { fontSize: 26, marginBottom: 8 };
const h2 = { fontSize: 18, marginBottom: 6, marginTop: 20 };
const p = { color: '#94a3b8', fontSize: 14, lineHeight: 1.7, margin: '4px 0' };
const li = { color: '#94a3b8', fontSize: 14, lineHeight: 1.7, margin: '4px 0 0 18px' };

export default function PrivacyPolicyPage() {
  return (
    <div style={container}>
      <div style={{ borderBottom: '1px solid #1f2937', padding: '16px 32px' }}>
        <span style={{ color: '#6366f1', fontWeight: 800, fontSize: 20 }}>SPECTRAINE</span>
      </div>

      <main style={card}>
        <header style={section}>
          <h1 style={h1}>Privacy Policy</h1>
          <p style={{ ...p, marginTop: 4 }}>Last updated: 26 March 2026</p>
          <p style={p}>
            This Privacy Policy explains how Spectraine (“we”, “us”, “our”) collects, uses, and protects personal data in connection with our AWS security and cost optimisation service.
          </p>
        </header>

        <section style={section}>
          <h2 style={h2}>1. Data we collect</h2>
          <p style={p}>We collect and process the following categories of data:</p>
          <ul style={{ paddingLeft: 0, listStyle: 'disc' }}>
            <li style={li}>
              <strong>Account data</strong> – name, email address, company, hashed password, billing status.
            </li>
            <li style={li}>
              <strong>Product usage data</strong> – scan history, findings, security scores, cost estimates, PDF reports.
            </li>
            <li style={li}>
              <strong>Technical data</strong> – IP address, browser/user agent, audit logs of key actions (login, billing changes, scans).
            </li>
            <li style={li}>
              <strong>AWS environment metadata</strong> – configuration and cost metadata retrieved via IAM roles you configure, but never your AWS access keys or secrets.
            </li>
          </ul>
        </section>

        <section style={section}>
          <h2 style={h2}>2. How we use your data</h2>
          <p style={p}>We use personal data strictly to:</p>
          <ul style={{ paddingLeft: 0, listStyle: 'disc' }}>
            <li style={li}>Provide and operate the Spectraine service, including AWS scans and reporting.</li>
            <li style={li}>Authenticate users, secure accounts, and prevent abuse.</li>
            <li style={li}>Process payments and manage subscriptions via Stripe.</li>
            <li style={li}>Send transactional emails (verification, password reset, scan notifications, billing updates).</li>
            <li style={li}>Improve and monitor the reliability and security of the platform.</li>
          </ul>
        </section>

        <section style={section}>
          <h2 style={h2}>3. AWS access and security</h2>
          <p style={p}>
            You connect Spectraine to your AWS account using an IAM role that you control. We assume this role using a unique External ID and read‑only permissions.
          </p>
          <ul style={{ paddingLeft: 0, listStyle: 'disc' }}>
            <li style={li}>We do not store your AWS access keys or root credentials.</li>
            <li style={li}>The IAM role can be revoked at any time from your AWS console.</li>
            <li style={li}>We only request permissions required to perform security posture and cost analysis.</li>
          </ul>
        </section>

        <section style={section}>
          <h2 style={h2}>4. Legal basis for processing (EEA/UK)</h2>
          <p style={p}>Where GDPR applies, we rely on the following legal bases:</p>
          <ul style={{ paddingLeft: 0, listStyle: 'disc' }}>
            <li style={li}>Performance of a contract – to deliver the Spectraine service to your organisation.</li>
            <li style={li}>Legitimate interests – to secure our platform, prevent abuse, and improve the product.</li>
            <li style={li}>Consent – where required for specific communications or optional features.</li>
          </ul>
        </section>

        <section style={section}>
          <h2 style={h2}>5. Data retention</h2>
          <p style={p}>
            We retain account and scan data while your subscription remains active and for up to 12 months afterwards, unless you request deletion earlier or a longer period is required by law or accounting rules.
          </p>
          <p style={p}>
            You can delete your account and associated data at any time using the in‑product account deletion controls, or by contacting us.
          </p>
        </section>

        <section style={section}>
          <h2 style={h2}>6. Sharing and subprocessors</h2>
          <p style={p}>We do not sell personal data. We may share limited data with trusted subprocessors solely to operate the service, for example:</p>
          <ul style={{ paddingLeft: 0, listStyle: 'disc' }}>
            <li style={li}>Cloud hosting and database providers.</li>
            <li style={li}>Email delivery providers for transactional messages.</li>
            <li style={li}>Stripe for payment processing and subscription billing.</li>
          </ul>
          <p style={p}>All subprocessors are bound by contractual obligations to protect your data.</p>
        </section>

        <section style={section}>
          <h2 style={h2}>7. International transfers</h2>
          <p style={p}>
            Where data is transferred outside the UK or EEA, we use appropriate safeguards such as Standard Contractual Clauses or equivalent protections required by applicable law.
          </p>
        </section>

        <section style={section}>
          <h2 style={h2}>8. Your rights</h2>
          <p style={p}>Depending on your location, you may have the right to:</p>
          <ul style={{ paddingLeft: 0, listStyle: 'disc' }}>
            <li style={li}>Access the personal data we hold about you.</li>
            <li style={li}>Correct inaccurate or incomplete data.</li>
            <li style={li}>Request deletion of your data (subject to legal obligations).</li>
            <li style={li}>Object to or restrict certain types of processing.</li>
            <li style={li}>Export your data in a portable format.</li>
          </ul>
          <p style={p}>
            You can exercise many of these rights directly from your account (data export and deletion), or by contacting us using the details below.
          </p>
        </section>

        <section style={section}>
          <h2 style={h2}>9. Security</h2>
          <p style={p}>
            We use technical and organisational measures to protect your data, including encrypted transport (HTTPS), access controls, audit logs, and least‑privilege AWS access via IAM roles.
          </p>
        </section>

        <section style={section}>
          <h2 style={h2}>10. Contact</h2>
          <p style={p}>
            If you have questions about this Privacy Policy or how we handle personal data, contact:
          </p>
          <p style={p}>
            Email: <a href="mailto:andrei@spectraine.com" style={{ color: '#6366f1' }}>andrei@spectraine.com</a>
          </p>
        </section>
      </main>
    </div>
  );
}